import React, { useState, useEffect, useRef } from 'react';
import { 
  Image, 
  Plus, 
  Edit3, 
  Trash2, 
  X, 
  Save, 
  Upload,
  ExternalLink,
  Calendar,
  Tag,
  Eye,
  Filter
} from 'lucide-react';
import { useTalentAuth } from '../../contexts/TalentAuthContext';
import PriceRangeSlider from '../public/PriceRangeSlider';
import './TalentPortfolio.css';

interface PortfolioItem {
  id: string;
  title: string;
  description: string;
  category: string;
  image: string;
  projectUrl?: string;
  tags: string[];
  date: string;
  featured: boolean;
  budget?: number;
  priceRange?: {
    min: number;
    max: number;
  };
}

interface AddPortfolioForm {
  title: string;
  description: string;
  category: string;
  image: string;
  projectUrl: string;
  tags: string;
  date: string;
  featured: boolean;
  budget: number;
  priceRangeMin: number;
  priceRangeMax: number;
}

const TalentPortfolio: React.FC = () => {
  const { talent } = useTalentAuth();
  const [portfolioItems, setPortfolioItems] = useState<PortfolioItem[]>([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingItem, setEditingItem] = useState<PortfolioItem | null>(null);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState<AddPortfolioForm>({
    title: '',
    description: '',
    category: '',
    image: '',
    projectUrl: '',
    tags: '',
    date: new Date().toISOString().split('T')[0],
    featured: false,
    budget: 0,
    priceRangeMin: 0,
    priceRangeMax: 0
  });
  const [selectedCategory, setSelectedCategory] = useState<string>('All');
  const [previewImage, setPreviewImage] = useState<string>('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // Price filter state
  const [priceRange, setPriceRange] = useState({ min: 0, max: 5000 });
  const [selectedPriceRange, setSelectedPriceRange] = useState({ min: 0, max: 5000 });
  const [showFilters, setShowFilters] = useState(false);

  const categories = ['All', 'Web Development', 'Mobile Apps', 'Design', 'Photography', 'Writing', 'Video', 'Other'];

  useEffect(() => {
    loadPortfolio();
  }, []);

  const loadPortfolio = async () => {
    setLoading(true);
    try {
      const response = await fetch('/api/talent/auth/portfolio', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('skillseek_talent_token')}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setPortfolioItems(data.portfolio || []);
      } else {
        // Load mock data for demo
        const mockData = [
          {
            id: '1',
            title: 'E-commerce Website',
            description: 'Modern e-commerce platform built with React and Node.js. Features include user authentication, payment integration, and admin dashboard.',
            category: 'Web Development',
            image: 'https://via.placeholder.com/400x300',
            projectUrl: 'https://example.com',
            tags: ['React', 'Node.js', 'MongoDB', 'Stripe'],
            date: '2024-01-15',
            featured: true,
            budget: 2500,
            priceRange: { min: 2000, max: 3000 }
          },
          {
            id: '2',
            title: 'Brand Identity Design',
            description: 'Complete brand identity package including logo, color palette, typography, and brand guidelines for a tech startup.',
            category: 'Design',
            image: 'https://via.placeholder.com/400x300',
            projectUrl: '',
            tags: ['Logo Design', 'Brand Identity', 'Adobe Illustrator'],
            date: '2024-01-10',
            featured: false,
            budget: 800,
            priceRange: { min: 600, max: 1000 }
          },
          {
            id: '3',
            title: 'Mobile App Development',
            description: 'Cross-platform mobile app for fitness tracking with real-time analytics and social features.',
            category: 'Mobile Apps',
            image: 'https://via.placeholder.com/400x300',
            projectUrl: 'https://example-app.com',
            tags: ['React Native', 'Firebase', 'Redux'],
            date: '2024-01-05',
            featured: true,
            budget: 4200,
            priceRange: { min: 3500, max: 4500 }
          },
          {
            id: '4',
            title: 'Photography Portfolio',
            description: 'Professional photography portfolio website with gallery, booking system, and client management.',
            category: 'Photography',
            image: 'https://via.placeholder.com/400x300',
            projectUrl: 'https://photo-portfolio.com',
            tags: ['WordPress', 'Custom Theme', 'Lightroom'],
            date: '2024-01-12',
            featured: false,
            budget: 1200,
            priceRange: { min: 1000, max: 1500 }
          }
        ];
        
        setPortfolioItems(mockData);
        
        // Calculate price range from all portfolio items
        if (mockData.length > 0) {
          const budgets = mockData.map(item => item.budget || 0);
          const minBudget = Math.min(...budgets);
          const maxBudget = Math.max(...budgets);
          setPriceRange({ min: minBudget, max: maxBudget });
          setSelectedPriceRange({ min: minBudget, max: maxBudget });
        }
      }
    } catch (error) {
      console.error('Error loading portfolio:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: keyof AddPortfolioForm, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        const result = reader.result as string;
        setPreviewImage(result);
        setFormData(prev => ({
          ...prev,
          image: result
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const portfolioItem: PortfolioItem = {
        id: editingItem?.id || Date.now().toString(),
        title: formData.title,
        description: formData.description,
        category: formData.category,
        image: formData.image,
        projectUrl: formData.projectUrl,
        tags: formData.tags.split(',').map(tag => tag.trim()).filter(tag => tag),
        date: formData.date,
        featured: formData.featured,
        budget: formData.budget,
        priceRange: {
          min: formData.priceRangeMin,
          max: formData.priceRangeMax
        }
      };

      if (editingItem) {
        setPortfolioItems(prev => prev.map(item => 
          item.id === editingItem.id ? portfolioItem : item
        ));
      } else {
        setPortfolioItems(prev => [...prev, portfolioItem]);
      }

      // Reset form
      setFormData({
        title: '',
        description: '',
        category: '',
        image: '',
        projectUrl: '',
        tags: '',
        date: new Date().toISOString().split('T')[0],
        featured: false,
        budget: 0,
        priceRangeMin: 0,
        priceRangeMax: 0
      });
      setPreviewImage('');
      setShowAddForm(false);
      setEditingItem(null);
    } catch (error) {
      console.error('Error saving portfolio item:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (item: PortfolioItem) => {
    setEditingItem(item);
    setFormData({
      title: item.title,
      description: item.description,
      category: item.category,
      image: item.image,
      projectUrl: item.projectUrl || '',
      tags: item.tags.join(', '),
      date: item.date,
      featured: item.featured,
      budget: item.budget || 0,
      priceRangeMin: item.priceRange?.min || 0,
      priceRangeMax: item.priceRange?.max || 0
    });
    setPreviewImage(item.image);
    setShowAddForm(true);
  };

  const handleDelete = (id: string) => {
    if (window.confirm('Are you sure you want to delete this portfolio item?')) {
      setPortfolioItems(prev => prev.filter(item => item.id !== id));
    }
  };

  const handleCancel = () => {
    setShowAddForm(false);
    setEditingItem(null);
    setFormData({
      title: '',
      description: '',
      category: '',
      image: '',
      projectUrl: '',
      tags: '',
      date: new Date().toISOString().split('T')[0],
      featured: false,
      budget: 0,
      priceRangeMin: 0,
      priceRangeMax: 0
    });
    setPreviewImage('');
  };

  // Filter items based on category and price range
  const filteredItems = portfolioItems.filter(item => {
    // Filter by category
    const categoryMatch = selectedCategory === 'All' || item.category === selectedCategory;
    
    // Filter by price range
    const priceMatch = !item.budget || (
      item.budget >= selectedPriceRange.min && 
      item.budget <= selectedPriceRange.max
    );
    
    return categoryMatch && priceMatch;
  });

  const featuredItems = portfolioItems.filter(item => item.featured);

  // Handle price range change
  const handlePriceRangeChange = (min: number, max: number) => {
    setSelectedPriceRange({ min, max });
  };

  // Clear all filters
  const clearFilters = () => {
    setSelectedCategory('All');
    setSelectedPriceRange({ min: priceRange.min, max: priceRange.max });
  };

  return (
    <div className="talent-portfolio">
      {/* Header */}
      <div className="portfolio-header">
        <div className="header-content">
          <h1>Portfolio</h1>
          <p>Showcase your best work and projects</p>
        </div>
        <button 
          className="btn btn-primary"
          onClick={() => setShowAddForm(true)}
        >
          <Plus size={20} />
          Add Portfolio Item
        </button>
      </div>

      {/* Stats */}
      <div className="portfolio-stats">
        <div className="stat-item">
          <div className="stat-number">{portfolioItems.length}</div>
          <div className="stat-label">Total Projects</div>
        </div>
        <div className="stat-item">
          <div className="stat-number">{featuredItems.length}</div>
          <div className="stat-label">Featured Projects</div>
        </div>
        <div className="stat-item">
          <div className="stat-number">{new Set(portfolioItems.map(item => item.category)).size}</div>
          <div className="stat-label">Categories</div>
        </div>
      </div>

      {/* Filters Section */}
      <div className="filters-section">
        <div className="filters-header">
          <h3>Filters</h3>
          <div className="filters-controls">
            <button 
              className="btn btn-secondary filter-toggle"
              onClick={() => setShowFilters(!showFilters)}
            >
              <Filter size={18} />
              {showFilters ? 'Hide Filters' : 'Show Filters'}
            </button>
            <button 
              className="btn btn-secondary clear-filters"
              onClick={clearFilters}
            >
              Clear All
            </button>
          </div>
        </div>
        
        <div className={`filters-content ${showFilters ? 'show' : ''}`}>
          <div className="filter-row">
            {/* Category Filter */}
            <div className="filter-group">
              <h4>Category</h4>
              <div className="category-filter">
                {categories.map(category => (
                  <button
                    key={category}
                    className={`category-btn ${selectedCategory === category ? 'active' : ''}`}
                    onClick={() => setSelectedCategory(category)}
                  >
                    {category}
                  </button>
                ))}
              </div>
            </div>
            
            {/* Price Filter */}
            <div className="filter-group">
              <div className="price-filter">
                <PriceRangeSlider
                  min={priceRange.min}
                  max={priceRange.max}
                  value={selectedPriceRange}
                  onChange={handlePriceRangeChange}
                />
              </div>
            </div>
          </div>
        </div>
        
        <div className="filter-results">
          <span>Showing {filteredItems.length} of {portfolioItems.length} projects</span>
        </div>
      </div>

      {/* Portfolio Grid */}
      {loading ? (
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading portfolio...</p>
        </div>
      ) : filteredItems.length === 0 ? (
        <div className="empty-state">
          <Image size={64} />
          <h3>No Portfolio Items</h3>
          <p>
            {selectedCategory === 'All' 
              ? "You haven't added any portfolio items yet. Click 'Add Portfolio Item' to get started."
              : `No items found in the ${selectedCategory} category.`
            }
          </p>
          {selectedCategory !== 'All' && (
            <button 
              className="btn btn-secondary"
              onClick={() => setSelectedCategory('All')}
            >
              View All Items
            </button>
          )}
        </div>
      ) : (
        <div className="portfolio-grid">
          {filteredItems.map(item => (
            <div key={item.id} className="portfolio-card">
              {item.featured && (
                <div className="featured-badge">Featured</div>
              )}
              <div className="card-image">
                <img src={item.image} alt={item.title} />
                <div className="card-overlay">
                  <div className="overlay-actions">
                    <button 
                      className="action-btn"
                      onClick={() => handleEdit(item)}
                      title="Edit"
                    >
                      <Edit3 size={16} />
                    </button>
                    <button 
                      className="action-btn"
                      onClick={() => handleDelete(item.id)}
                      title="Delete"
                    >
                      <Trash2 size={16} />
                    </button>
                    {item.projectUrl && (
                      <a 
                        href={item.projectUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="action-btn"
                        title="View Project"
                      >
                        <ExternalLink size={16} />
                      </a>
                    )}
                  </div>
                </div>
              </div>
              <div className="card-content">
                <div className="card-header">
                  <h3>{item.title}</h3>
                  <span className="category-badge">{item.category}</span>
                </div>
                <p className="card-description">{item.description}</p>
                <div className="card-meta">
                  <div className="meta-item">
                    <Calendar size={14} />
                    <span>{new Date(item.date).toLocaleDateString()}</span>
                  </div>
                  {item.budget && (
                    <div className="meta-item">
                      <span className="budget-badge">${item.budget}</span>
                    </div>
                  )}
                  {item.tags.length > 0 && (
                    <div className="meta-item">
                      <Tag size={14} />
                      <span>{item.tags.slice(0, 2).join(', ')}</span>
                      {item.tags.length > 2 && <span> +{item.tags.length - 2}</span>}
                    </div>
                  )}
                </div>
                <div className="card-tags">
                  {item.tags.slice(0, 3).map(tag => (
                    <span key={tag} className="tag">{tag}</span>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add/Edit Form Modal */}
      {showAddForm && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h2>{editingItem ? 'Edit Portfolio Item' : 'Add Portfolio Item'}</h2>
              <button className="close-btn" onClick={handleCancel}>
                <X size={24} />
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="portfolio-form">
              <div className="form-row">
                <div className="form-group">
                  <label>Title *</label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => handleInputChange('title', e.target.value)}
                    placeholder="Project title"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Category *</label>
                  <select
                    value={formData.category}
                    onChange={(e) => handleInputChange('category', e.target.value)}
                    required
                  >
                    <option value="">Select category</option>
                    {categories.slice(1).map(category => (
                      <option key={category} value={category}>{category}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label>Description *</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  placeholder="Describe your project..."
                  rows={4}
                  required
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Project URL</label>
                  <input
                    type="url"
                    value={formData.projectUrl}
                    onChange={(e) => handleInputChange('projectUrl', e.target.value)}
                    placeholder="https://example.com"
                  />
                </div>
                <div className="form-group">
                  <label>Date *</label>
                  <input
                    type="date"
                    value={formData.date}
                    onChange={(e) => handleInputChange('date', e.target.value)}
                    required
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Project Budget ($)</label>
                  <input
                    type="number"
                    value={formData.budget}
                    onChange={(e) => handleInputChange('budget', parseFloat(e.target.value) || 0)}
                    placeholder="e.g., 2500"
                    min="0"
                    step="100"
                  />
                </div>
                <div className="form-group">
                  <label>Price Range</label>
                  <div className="price-range-inputs">
                    <input
                      type="number"
                      value={formData.priceRangeMin}
                      onChange={(e) => handleInputChange('priceRangeMin', parseFloat(e.target.value) || 0)}
                      placeholder="Min"
                      min="0"
                      step="100"
                    />
                    <span>-</span>
                    <input
                      type="number"
                      value={formData.priceRangeMax}
                      onChange={(e) => handleInputChange('priceRangeMax', parseFloat(e.target.value) || 0)}
                      placeholder="Max"
                      min="0"
                      step="100"
                    />
                  </div>
                </div>
              </div>

              <div className="form-group">
                <label>Tags</label>
                <input
                  type="text"
                  value={formData.tags}
                  onChange={(e) => handleInputChange('tags', e.target.value)}
                  placeholder="React, Node.js, MongoDB (comma separated)"
                />
              </div>

              <div className="form-group">
                <label>Project Image *</label>
                <div className="image-upload">
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleImageUpload}
                    accept="image/*"
                    className="file-input"
                  />
                  <div className="upload-area" onClick={() => fileInputRef.current?.click()}>
                    {previewImage ? (
                      <img src={previewImage} alt="Preview" className="preview-image" />
                    ) : (
                      <div className="upload-placeholder">
                        <Upload size={32} />
                        <p>Click to upload image</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={formData.featured}
                    onChange={(e) => handleInputChange('featured', e.target.checked)}
                  />
                  <span>Featured project</span>
                </label>
              </div>

              <div className="form-actions">
                <button type="button" className="btn btn-secondary" onClick={handleCancel}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={loading}>
                  <Save size={18} />
                  {loading ? 'Saving...' : (editingItem ? 'Update' : 'Add')} Portfolio Item
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default TalentPortfolio; 